import { z } from 'zod';
import { Gauge } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { useDashboardMetric } from '@/hooks/use-dashboard-metric';
import type { TileDefinition } from './types';

/**
 * `outbound_efficiency` — a compact 2×2 of spend/effort numbers over the
 * calls concluded in the window. Not a StatTile (it shows four figures),
 * but visually consistent: muted labels, tabular-nums values. Nulls render
 * as "—" so a window with no bookings/calls doesn't show a bogus 0 or NaN.
 */
export const outboundEfficiencyTile: TileDefinition = {
  type: 'outbound_efficiency',
  title: 'Call efficiency',
  description: 'Average duration, cost, cost per booking, and attempts.',
  icon: Gauge,
  metrics: ['outbound_efficiency'],
  defaultSize: { w: 4, h: 2 },
  minSize: { w: 3, h: 2 },
  configSchema: z.object({}).strict(),
  defaultConfig: {},
  render: ({ period, enabled }) => {
    const { metric, isLoading, isError } = useDashboardMetric({
      metricKey: 'outbound_efficiency',
      from: period.from,
      to: period.to,
      enabled,
    });

    if (isLoading) {
      return (
        <div className="grid h-full grid-cols-2 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-10" />
          ))}
        </div>
      );
    }
    if (isError) {
      return (
        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
          Failed to load
        </div>
      );
    }

    const cells: Array<{ label: string; value: string }> = [
      { label: 'Avg duration', value: formatDuration(metric?.avgDurationSeconds ?? null) },
      { label: 'Total cost', value: formatCurrency(metric?.totalCost ?? 0) },
      { label: 'Cost / booking', value: formatCurrency(metric?.costPerBooking ?? null) },
      { label: 'Avg attempts', value: formatAttempts(metric?.avgAttempts ?? null) },
    ];

    return (
      <div className="grid h-full grid-cols-2 content-center gap-x-4 gap-y-3">
        {cells.map((c) => (
          <div key={c.label} className="flex flex-col">
            <span className="text-xs text-muted-foreground">{c.label}</span>
            <span className="text-xl font-semibold tabular-nums">{c.value}</span>
          </div>
        ))}
      </div>
    );
  },
};

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatCurrency(value: number | null): string {
  if (value == null) return '—';
  return `$${value.toFixed(2)}`;
}

function formatAttempts(value: number | null): string {
  if (value == null) return '—';
  return value.toFixed(1);
}
